import { AHIcon, Box, useSpaceSize } from "arkh";
import { useFragment, graphql } from "react-relay";
import { FlatList, Platform, View } from "react-native";
import { TeamFeatureBar_team$key } from "./__generated__/TeamFeatureBar_team.graphql";
import { Suspense, useCallback } from "react";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { TeamFeatureItem } from "./TeamFeatureItem";
import { TeamFeatureBarTodoCategoryItem } from "./TeamFeatureBarTodoCategoryItem";
import { TeamFeatureBarKPIItem } from "./TeamFeatureBarKPIItem";
import { TeamFeatureBarProductItem } from "./TeamFeatureBarProductItem";
import { TeamFeatureBarProductDateTrackerItem } from "./TeamFeatureBarProductDateTrackerItem";
import { TeamFeatureBarSignalItem } from "./TeamFeatureBarSignalItem";

export function TeamFeatureBar(props: {
  team: TeamFeatureBar_team$key;
  onOpenTodoCategory: (categoryId: string, categoryName: string) => void;
  onOpenFeature: (
    feature:
      | "KPI"
      | "FILE"
      | "SIGNAL"
      | "EMPLOYEE_SCHEDULE"
      | "PRODUCT"
      | "PRODUCT_DATE_TRACKER"
      | "CALENDAR"
  ) => void;
  hideSignals?: boolean;
  profileIndicator?: React.ReactNode;
}) {
  const team = useFragment(
    graphql`
      fragment TeamFeatureBar_team on Team {
        id
        features {
          ... on TodoCategoryTeamFeature {
            id
            todoCategoryTeam {
              ...TeamFeatureBarTodoCategoryItem_todoCategoryTeam
            }
            __typename
          }
          ... on FileTeamFeature {
            __typename
          }
          ... on SignalTeamFeature {
            __typename
            ...TeamFeatureBarSignalItem_team @defer
          }
          ... on CalendarTeamFeature {
            __typename
          }
          ... on KPITeamFeature {
            ...TeamFeatureBarKPIItem_KPITeamFeature
            __typename
          }
          ... on ProductTeamFeature {
            __typename
          }
          ... on ProductDateTrackerTeamFeature {
            ...TeamFeatureBarProductDateTrackerItem_productDateTrackerTeamFeature
              @defer

            __typename
          }
          ... on EmployeeScheduleTeamFeature {
            __typename
          }
        }
      }
    `,
    props.team
  );

  const size = useSpaceSize("medium");

  const intl_tokens = useIntlTokens();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  const isGrid = Platform.OS === "web" || !isCompact;

  const items = props.hideSignals
    ? team.features.filter((f) => f.__typename !== "SignalTeamFeature")
    : team.features;

  const renderItem = useCallback(
    ({ item }: { item: (typeof items)[number] }) => {
      switch (item.__typename) {
        case "CalendarTeamFeature":
          return (
            <TeamFeatureItem
              key={item.__typename}
              isGrid={isGrid}
              onPress={() => {
                props.onOpenFeature("CALENDAR");
              }}
              icon={<AHIcon type="calendar" size="medium" />}
              title={intl_tokens.Calendar}
            />
          );
        case "TodoCategoryTeamFeature":
          return (
            <TeamFeatureBarTodoCategoryItem
              key={item.id}
              isGrid={isGrid}
              todoCategoryTeam={item.todoCategoryTeam}
              onPress={props.onOpenTodoCategory}
              profileIndicator={props.profileIndicator}
            />
          );

        case "KPITeamFeature":
          return (
            <TeamFeatureBarKPIItem
              key={item.__typename}
              feature={item}
              isGrid={isGrid}
              onPress={() => {
                props.onOpenFeature("KPI");
              }}
            />
          );

        case "FileTeamFeature":
          return (
            <TeamFeatureItem
              key={item.__typename}
              isGrid={isGrid}
              icon={<AHIcon type="files" size="medium" />}
              title={intl_tokens.Files}
              onPress={() => {
                props.onOpenFeature("FILE");
              }}
            />
          );
        case "EmployeeScheduleTeamFeature":
          return (
            <TeamFeatureItem
              key={item.__typename}
              isGrid={isGrid}
              icon={<AHIcon type="schedule" size="medium" />}
              title={intl_tokens.EmployeeSchedule}
              onPress={() => {
                props.onOpenFeature("EMPLOYEE_SCHEDULE");
              }}
            />
          );

        case "SignalTeamFeature":
          return (
            <Suspense fallback={<Box />}>
              <TeamFeatureBarSignalItem
                key={item.__typename}
                isGrid={isGrid}
                onOpenFeature={() => {
                  props.onOpenFeature("SIGNAL");
                }}
                feature={item}
              />
            </Suspense>
          );
        case "ProductTeamFeature":
          return (
            <TeamFeatureBarProductItem
              key={item.__typename}
              isGrid={isGrid}
              onPress={() => {
                props.onOpenFeature("PRODUCT");
              }}
            />
          );
        case "ProductDateTrackerTeamFeature":
          return (
            <TeamFeatureBarProductDateTrackerItem
              key={item.__typename}
              isGrid={isGrid}
              onPress={() => {
                props.onOpenFeature("PRODUCT_DATE_TRACKER");
              }}
              productDateTrackerFeature={item}
            />
          );
        default:
          return null;
      }
    },
    [
      intl_tokens.Calendar,
      intl_tokens.EmployeeSchedule,
      intl_tokens.Files,
      isGrid,
      props,
    ]
  );

  if (Platform.OS !== "web" && isCompact) {
    return (
      <View>
        <FlatList
          horizontal
          contentContainerStyle={{ paddingLeft: size, paddingBottom: size }}
          data={items}
          renderItem={renderItem}
          showsHorizontalScrollIndicator={false}
        />
      </View>
    );
  }

  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        paddingLeft: size,
        paddingBottom: size,
      }}
    >
      {items.map((item) => renderItem({ item }))}
    </Box>
  );
}
